// Typed fetch wrapper. One module talks to the backend; pages and components
// import named functions, not raw fetch. Auth header is injected from
// localStorage so any page can call any endpoint without thinking about it.

export const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

const TOKEN_STORAGE_KEY = "scholarpetition.token";

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function getToken(): string | null {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setToken(token: string | null) {
  if (token === null) localStorage.removeItem(TOKEN_STORAGE_KEY);
  else localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function authToken(): string | null {
  return getToken();
}

export async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; auth?: boolean } = {}
): Promise<T> {
  const { method = "GET", body, auth = true } = options;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get("content-type") ?? "";
  const parsed: unknown = contentType.includes("application/json")
    ? await res.json()
    : await res.text();
  if (!res.ok) {
    const detail =
      typeof parsed === "object" && parsed !== null && "detail" in parsed
        ? (parsed as { detail: unknown }).detail
        : parsed;
    const message =
      typeof detail === "string" ? detail : `Request failed (${res.status})`;
    throw new ApiError(res.status, parsed, message);
  }
  return parsed as T;
}

// -- Types matching the FastAPI schemas --

export interface HealthOut {
  status: "ok" | "degraded";
  version: string;
  environment: string;
  demo_mode: boolean;
  email_configured: boolean;
}

export const health = {
  get: () => request<HealthOut>("/v1/healthz", { auth: false }),
};

// Public attorney-share endpoints — reached via token, no auth required.
export const share = {
  caseSummary: (token: string) =>
    request<CaseSummaryOut>(`/v1/share/${token}/case-summary`, { auth: false }),
  packetUrl: (token: string) => `${API_BASE_URL}/v1/share/${token}/packet`,
};

export interface TenantOut {
  id: string;
  slug: string;
  display_name: string;
  kind: "scholar" | "attorney" | "partner_workspace";
  distribution_channel: "direct" | "partner";
  partner_slug: string | null;
  is_marketplace_listed: boolean;
  config: Record<string, unknown>;
  subscription_status: string;
  is_paying: boolean;
  has_stripe_customer: boolean;
}

export const tenants = {
  me: () => request<TenantOut>("/v1/tenants/me"),
};

export interface TokenOut {
  access_token: string;
  token_type: string;
  expires_at: string;
  tenant_id: string;
  tenant_display_name: string;
  user_email: string;
  role: string;
}

export interface ScholarProfileOut {
  id: string;
  full_name: string;
  preferred_name: string | null;
  email: string | null;
  country_of_birth: string | null;
  country_of_nationality: string | null;
  current_institution: string | null;
  current_title: string | null;
  field_of_study: string | null;
  education_history: Array<Record<string, unknown>>;
  employment_history: Array<Record<string, unknown>>;
  notes: string | null;
  google_scholar_user_id: string | null;
}

export interface ApplicationOut {
  id: string;
  application_type: "eb1a" | "eb2_niw" | "h1b" | "o1a";
  status: string;
  title: string;
  scholar_profile_id: string;
  subtype_data: Record<string, unknown>;
}

export interface CaseSetup {
  plan: "silver" | "gold" | "platinum" | null;
  premium_processing: "yes" | "no" | "undecided" | null;
  /** asap (about two weeks) · target (a date in mind) · flexible (no date
   *  yet). "deadline" is legacy and reads as target. Null on setups saved
   *  before the choice existed. */
  filing_timing?: "asap" | "deadline" | "target" | "flexible" | null;
  /** ISO date (YYYY-MM-DD) for a deadline or target, else null. */
  target_filing_date: string | null;
  completed: boolean;
}

/** GET/PATCH /v1/applications/{id}/progress — the Case Plan's state. */
export interface CaseProgressOut {
  done_stages: string[];
  setup: CaseSetup;
  scan_result: Record<string, unknown> | null;
  scholar_url: string | null;
  /** Days each timeline milestone was nudged by (lib/timeline.ts ids). */
  milestone_overrides?: Record<string, number>;
  /** ISO date the recommended timeline counts from; null means today. */
  timeline_start?: string | null;
  /** The Evidence wizard's answers (with a savedAt timestamp). */
  evidence_state?: Record<string, unknown> | null;
  /** Letter Studio's saved flow and role flow ({ savedAt, flow, role }). */
  letters_state?: Record<string, unknown> | null;
}

export interface CaseProgressPatch {
  add_done?: string[];
  remove_done?: string[];
  setup?: CaseSetup;
  scan_result?: Record<string, unknown>;
  scholar_url?: string;
  milestone_overrides?: Record<string, number>;
  timeline_start?: string;
  evidence_state?: Record<string, unknown>;
  letters_state?: Record<string, unknown>;
}

export interface EvidenceOut {
  id: string;
  application_id: string;
  kind: string;
  label: string;
  /** Null for reference items (e.g. citing works pulled from OpenAlex). */
  file_name: string | null;
  content_type: string | null;
  size_bytes: number | null;
  /** "upload" | "openalex" | "seed" */
  source?: string;
  parse_status: string;
  parse_error: string | null;
  parsed_metadata: Record<string, unknown>;
  download_url: string | null;
  prestige_url: string | null;
}

export interface CitingWorksAutoOut {
  created: number;
  skipped_existing: number;
  total_citing_works: number;
  papers_resolved: number;
  budget_exhausted: boolean;
  items: EvidenceOut[];
}

export interface OpenAlexImportOut {
  author_id: string;
  author_name: string;
  matched_by: string;
  works_found: number;
  inserted: number;
  updated: number;
  budget_exhausted: boolean;
}

export interface CriterionAssessmentOut {
  id: string;
  application_id: string;
  criterion_key: string;
  score: "strong" | "moderate" | "weak" | "missing";
  rationale: string | null;
  supporting_evidence_ids: string[];
  extra: Record<string, unknown>;
}

export interface CriterionCatalogEntry {
  key: string;
  short_title: string;
  description: string;
  citation: string;
}

export interface RecommenderOut {
  id: string;
  full_name: string;
  title: string | null;
  institution: string | null;
  country: string | null;
  email: string | null;
  relationship: "independent" | "dependent";
  expertise_summary: string | null;
  /** Longer background on the writer — pasted by the scholar or
   *  extracted from a profile link / CV. Read by the letter drafter. */
  bio: string | null;
  /** Independent-citation signal — drives Letter Studio's
   *  "Cited your work N×" arms-length overline. Null when unknown. */
  cited_count: number | null;
  /** Free-text explanation of the relationship signal
   *  ("Reviewed your 2024 Nature paper", "Co-author on 3 papers").
   *  Falls back to expertise_summary or a generic group line. */
  relationship_evidence: string | null;
}

export interface ProposedEditOut {
  id: string;
  old_text: string;
  new_text: string;
  reason: string;
}

export interface SuggestEditOut {
  summary: string;
  edits: ProposedEditOut[];
}

/** Which letter a RecommendationLetter row is. Letter Studio drafts the
 *  two kinds in separate flows and never mixes them:
 *    contributions — the original-contributions advisory opinion (v),
 *                    written by a peer or an arms-length expert;
 *    critical_role — the employer letter for the leading/critical-role
 *                    criterion (viii), written by a supervisor. */
export type LetterKind = "contributions" | "critical_role";

/** Which letters a list / delete-all call covers. The server defaults to
 *  "petition" (the original petition's letters), so a caller that passes
 *  nothing never sees a letter drafted for an RFE response.
 *    petition — rfe_case_id is null
 *    rfe      — drafted for an RFE response (narrow with rfeCaseId)
 *    all      — both */
export type LetterScope = "petition" | "rfe" | "all";

export interface LetterQuery {
  scope?: LetterScope;
  /** With scope "rfe": only this RFE response's letters. */
  rfeCaseId?: string;
}

function letterQuery(kind?: LetterKind, q: LetterQuery = {}): string {
  const params = new URLSearchParams();
  if (kind) params.set("kind", kind);
  if (q.scope) params.set("scope", q.scope);
  if (q.rfeCaseId) params.set("rfe_case_id", q.rfeCaseId);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** Questionnaire answers behind a critical-role letter. Free-form JSON on
 *  the backend; this is the shape the Letter Studio role form writes and
 *  the prompt builder reads. */
export interface CriticalRoleInputs {
  role: { employer: string; unit: string; title: string; start: string; end: string; location: string };
  signatory: {
    name: string; title: string; relationship: string; email: string;
    address: string; phone: string; bio: string;
  };
  /** Bullet lines, one duty per line. */
  duties: string;
  unit_staffing: { headcount: string; roles: string; beneficiary_alone: string };
  examples: Array<{
    problem: string; why_it_mattered: string; implications: string;
    what_beneficiary_did: string; expertise: string; outcome_numbers: string;
    us_importance: string;
  }>;
  corroboration: Array<{ evidence_item_id: string; label: string }>;
  reputation: { organization_markers: string; unit_markers: string };
}

export interface RecommendationLetterOut {
  id: string;
  application_id: string;
  recommender_id: string;
  status: string;
  /** Defaults to "contributions" for rows created before kinds existed. */
  kind: LetterKind;
  /** The RFE response this letter was drafted for; null for a letter in
   *  the original petition. */
  rfe_case_id: string | null;
  /** Critical-role questionnaire answers; null on contributions letters. */
  inputs: Record<string, unknown> | null;
  body_markdown: string | null;
  has_docx: boolean;
  // Publication UUIDs the user has anchored this letter on. Empty list
  // means the backend's auto-pick rule will run at draft time.
  anchor_publication_ids: string[];
  // User-selected tone preset. Null = backend default
  // (knowledgeable_friend).
  tone: "knowledgeable_friend" | "formal_expert" | "direct_concise" | null;
  // Recommender outreach loop. Null until the scholar clicks
  // "Send to Prof. X for approval" / the recommender returns the letter.
  outreach_sent_at: string | null;
  recommender_returned_text: string | null;
  has_signed_pdf: boolean;
  received_at: string | null;
  /** Where the letter stands with its writer (one derivation, server-side). */
  writer_status: WriterStatus;
  /** First time the writer opened the optional in-app link. */
  outreach_viewed_at: string | null;
  created_at: string | null;
}

/** RecommendationLetterOut.writer_status, in lifecycle order. "opened"
 *  and "returned" only appear when the optional in-app link was used. */
export type WriterStatus =
  | "not_drafted"
  | "drafted"
  | "approved"
  | "sent"
  | "opened"
  | "returned"
  | "signed";

export interface RecommenderEnrichExtracted {
  title: string | null;
  institution: string | null;
  focus: string | null;
  bio: string | null;
  h_index: number | null;
  citations_all: number | null;
}

export interface RecommenderEnrichResponse {
  recommender: RecommenderOut;
  extracted: RecommenderEnrichExtracted;
  source: "scholar" | "web" | "cache" | "cv";
}

export interface RecommenderSuggestion {
  name: string;
  affiliations: string[];
  evidence_text: string;
  kind: "collaborator" | "independent";
  s2_url: string | null;
  most_recent_year: number | null;
  sample_paper_title: string | null;
  /** ISO 3166-1 alpha-2 country of the writer's institution, when known. */
  country?: string | null;
}

export interface RecommenderSuggestionsOut {
  collaborators: RecommenderSuggestion[];
  independents: RecommenderSuggestion[];
  no_data: boolean;
  reason: string | null;
  /** Soft callout shown next to the arms-length section when
   *  collaborators surfaced but independents did not — explains why
   *  every citer got filtered out (most often "all citers also
   *  co-authored with you"). */
  independents_reason: string | null;
  /** Per-run counts — surfaced to the user so they can see what
   *  changed between iterative Refresh clicks. */
  diagnostics?: {
    publications_total: number;
    publications_with_doi: number;
    papers_resolved_on_s2: number;
    total_citing_appearances: number;
    unique_citing_authors: number;
    citing_excluded_as_self: number;
    citing_excluded_as_coauthor: number;
    cache_age_seconds: number | null;
    scholar_surname_used: string | null;
  };
}

export interface PetitionLetterOut {
  id: string;
  application_id: string;
  status: string;
  body_markdown: string | null;
  has_docx: boolean;
  /** When the scholar accepted the brief (status "finalized"); null until then. */
  brief_accepted_at?: string | null;
  /** True when the VisaGenius case team accepted it on the client's behalf. */
  brief_accepted_by_operator?: boolean;
  /** Problems with the exhibit numbering (a number used twice, a gap, out of order). */
  exhibit_numbering_issues?: string[];
}

// -- API surface --

export const auth = {
  signIn: (body: { email: string; workspace_name?: string; referral_code?: string }) =>
    request<{ message: string }>("/v1/auth/sign-in", {
      method: "POST",
      body,
      auth: false,
    }),
  acceptInvite: (token: string, referral_code?: string) =>
    request<TokenOut>("/v1/auth/accept-invite", {
      method: "POST",
      body: { token, ...(referral_code ? { referral_code } : {}) },
      auth: false,
    }),
  verifyCode: (body: { email: string; code: string; referral_code?: string }) =>
    request<TokenOut>("/v1/auth/verify-code", {
      method: "POST",
      body,
      auth: false,
    }),
  google: (body: { credential: string; workspace_name?: string; referral_code?: string }) =>
    request<TokenOut>("/v1/auth/google", {
      method: "POST",
      body,
      auth: false,
    }),
};

// Refer-a-friend ---------------------------------------------------------
// Lifecycle: sent → signed_up → held (friend paid; 72-hour hold) → credited.
// A refund or dispute flips held/credited → voided. `checked_out` is the
// pre-hold terminal status (credited immediately) kept for old rows.
export type ReferralStatus =
  | "sent"
  | "signed_up"
  | "held"
  | "credited"
  | "voided"
  | "checked_out"
  | "expired";

export interface ReferralOut {
  id: string;
  referee_name: string;
  referee_email: string;
  status: ReferralStatus;
  created_at: string;
  signed_up_at: string | null;
  checked_out_at: string | null;
  /** Amount this referral is worth; 0 until the friend pays. */
  credit_cents: number;
  /** When the hold ends and the credit releases (held rows). */
  eligible_at: string | null;
  credited_at: string | null;
  voided_at: string | null;
  void_reason: string | null;
}

export interface ReferralListOut {
  referrals: ReferralOut[];
  /** Spendable credit on the caller's ledger (auto-applied at checkout). */
  credit_cents: number;
  credit_label: string;
  /** Credit still inside the hold window — not spendable yet. */
  pending_cents: number;
  /** Program constants — never hardcode these in the UI. */
  credit_amount_cents: number;
  hold_hours: number;
  terms_url: string;
  referral_link: string;
}

export const referrals = {
  create: (body: { referee_name: string; referee_email: string }) =>
    request<ReferralOut>("/v1/referrals", { method: "POST", body }),
  list: () => request<ReferralListOut>("/v1/referrals"),
  /** Admin only: release every held credit whose hold has elapsed. */
  release: () => request<{ released: number }>("/v1/referrals/release", { method: "POST" }),
};

export interface LeadIn {
  name: string;
  email: string;
  phone?: string | null;
  requested_slot?: string | null;
  stage?: string | null;
  note?: string | null;
  source?: string;
  referral_code?: string | null;
}

export interface LeadOut {
  id: string;
  created_at: string;
  message: string;
}

export const leads = {
  create: (body: LeadIn) => request<LeadOut>("/v1/leads", { method: "POST", body }),
  /** The plan the signed-in visitor chose on the website's pricing section. */
  planInterest: (body: { plan: "eb1a" | "eb1a_niw" | "attorney" | "o1a"; full_name?: string }) =>
    request<{ plan: string; recorded_on: string }>("/v1/leads/plan-interest", { method: "POST", body }),
};

/** Case Plan stages the demo seeder can seed one at a time. */
export type SeedDemoStage = "documents" | "i140" | "evidence";

/** POST /v1/scholar-profile/seed-demo body. No body == full, no letters. */
export interface SeedDemoIn {
  scope?: "full" | "stage";
  /** Required when scope is "stage". */
  stage?: SeedDemoStage | null;
  /** Full scope only: also seed recommenders + drafted letters. */
  include_letters?: boolean;
}

export interface SeedDemoResult {
  scholar_profile_id: string;
  application_id: string;
  publications_inserted: number;
  evidence_inserted: number;
  recommenders_inserted: number;
  assessments_inserted: number;
  letters_inserted?: number;
  i140_fields_filled?: number;
  persona_name?: string;
  scholar_url?: string | null;
  /** EligibilityScanOut shape — written to sp-scan-result by the seeder. */
  scan_result?: { criteria?: unknown[]; [k: string]: unknown } | null;
  scope?: "full" | "stage";
  stage?: SeedDemoStage | null;
  /** Case Plan stage ids to mark done (sp-done). */
  done_stages?: string[];
  conv_step?: number;
  presentations?: string[];
}

export const scholarProfile = {
  /** Wipe the caller's ScholarProfile + everything cascaded below it.
   *  Idempotent: returns 204 even when no profile exists. Backs the
   *  "Replay the flow from the start" affordance on the Case Plan. */
  reset: () =>
    request<void>("/v1/scholar-profile/reset", { method: "POST" }),
  get: () => request<ScholarProfileOut>("/v1/scholar-profile"),
  upsert: (body: Partial<ScholarProfileOut> & { full_name: string }) =>
    request<ScholarProfileOut>("/v1/scholar-profile", { method: "PUT", body }),
  seedDemo: (body?: SeedDemoIn) =>
    request<SeedDemoResult>("/v1/scholar-profile/seed-demo", { method: "POST", body }),
};

// Guided Scholar Path (EB-1A) — backs the card flow at /scholar-path.
export interface EB1AProfileOut {
  education: string[];
  employment: string[];
  awards: string[];
  patents: string[];
  peer_review: string[];
  presentations: string[];
  publications: string[];
  memberships: string[];
  press: string[];
}

export interface EB1ACriterionEvidence {
  id: string;
  strength: "strong" | "moderate" | "building" | "absent";
  evidenceCount: number;
  cvHits: string[];
  scholarHits: string[];
}

export const eb1a = {
  profile: () => request<EB1AProfileOut>("/v1/eb1a/profile"),
  evidence: () => request<EB1ACriterionEvidence[]>("/v1/eb1a/evidence"),
  saveAnswer: (criterionId: string, hasEvidence: boolean) =>
    request<{ answers: Record<string, boolean> }>("/v1/eb1a/answers", {
      method: "POST",
      body: { criterionId, hasEvidence },
    }),
  connectSource: (provider: "gdrive" | "dropbox" | "onedrive" | "box") =>
    request<{ connected: boolean; reason: string | null; auth_url: string | null }>(
      "/v1/eb1a/sources/connect",
      { method: "POST", body: { provider } },
    ),
};

export interface PaidStatusOut {
  paid: boolean;
  paywall_enabled: boolean;
}

export interface CheckoutResponse {
  url: string;
  session_id: string;
}

export interface CaseSummaryOut {
  display_name: string;
  field: string;
  institution: string;
  nationality: string;
  current_status: string;
  classification: string;
  title: string;
  status: string;
  concurrent_i485: string;
  premium_processing: string;
  paid_at: string | null;
  signed_letters: string[];
  draft_letters: string[];
  pending_letters: string[];
  evidence_by_kind: Record<string, number>;
  gaps: string[];
}

export interface ShareLinkOut {
  token: string;
  url: string;
  expires_in_days: number;
}

export const applications = {
  list: () => request<ApplicationOut[]>("/v1/applications"),
  get: (id: string) => request<ApplicationOut>(`/v1/applications/${id}`),
  paidStatus: (id: string) =>
    request<PaidStatusOut>(`/v1/applications/${id}/paid`),
  caseSummary: (id: string) =>
    request<CaseSummaryOut>(`/v1/applications/${id}/case-summary`),
  share: (id: string) =>
    request<ShareLinkOut>(`/v1/applications/${id}/share`, { method: "POST" }),
  checkout: (id: string) =>
    request<CheckoutResponse>(`/v1/applications/${id}/checkout`, {
      method: "POST",
    }),
  create: (body: { application_type: "eb1a" | "eb2_niw"; title: string }) =>
    request<ApplicationOut>("/v1/applications", { method: "POST", body }),
  patch: (id: string, body: Partial<{ title: string; status: string; subtype_data: Record<string, unknown> }>) =>
    request<ApplicationOut>(`/v1/applications/${id}`, { method: "PATCH", body }),
  progress: (id: string) => request<CaseProgressOut>(`/v1/applications/${id}/progress`),
  patchProgress: (id: string, body: CaseProgressPatch) =>
    request<CaseProgressOut>(`/v1/applications/${id}/progress`, { method: "PATCH", body }),
  /** URL for downloading the filled I-140 PDF. */
  i140PdfUrl: (id: string) => `${API_BASE_URL}/v1/applications/${id}/i140.pdf`,
  /** URL for downloading the filled I-907 PDF. */
  i907PdfUrl: (id: string) => `${API_BASE_URL}/v1/applications/${id}/i907.pdf`,
  /** URL for the free pre-paywall I-140 preview. */
  i140PreviewPdfUrl: (id: string) => `${API_BASE_URL}/v1/applications/${id}/i140-preview.pdf`,
  /** Top-N SOC matches for the I-140 occupation classification field.
   *  Reads scholar profile (current_title + field_of_study + degrees)
   *  on the server and runs the BLS OEWS fuzzy matcher. Returns an
   *  empty list when the profile is too sparse to score anything. */
  socMatch: (id: string, topN = 3) =>
    request<{
      matches: {
        soc: string;
        title: string;
        score: number;
        national_median: number;
        p10: number;
        p90: number;
      }[];
    }>(`/v1/applications/${id}/soc-match?top_n=${topN}`),
};

// ─── Google Drive folder import (Documents stage) ─────────────────────────────

export interface DriveImportStatusOut {
  configured: boolean;
  /** Address the user shares their folder with. Null when not configured. */
  service_account_email: string | null;
  last_folder_id: string | null;
  last_imported_at: string | null;
  last_file_count: number | null;
}

export interface DriveImportedFile {
  evidence_id: string;
  file_name: string;
  doc_type: string;
  slot: string | null;
  prefilled: string[];
}

export interface DriveImportOut {
  folder_id: string;
  imported: DriveImportedFile[];
  skipped: { file_name: string; reason: string }[];
  prefilled_fields: string[];
}

export const driveImport = {
  status: (applicationId: string) =>
    request<DriveImportStatusOut>(`/v1/applications/${applicationId}/drive-import`),
  run: (applicationId: string, folder: string) =>
    request<DriveImportOut>(`/v1/applications/${applicationId}/drive-import`, {
      method: "POST",
      body: { folder },
    }),
};

/** One value the document reader pre-filled on the I-140 questionnaire
 *  (Application.subtype_data.document_prefill[<key>]). */
export interface DocumentPrefillEntry {
  value: string;
  source: string;
  confidence: number;
  verified: boolean;
  evidence_id: string;
  extracted_at: string;
}

export const evidence = {
  list: (applicationId: string) =>
    request<EvidenceOut[]>(`/v1/applications/${applicationId}/evidence`),
  upload: async (
    applicationId: string,
    file: File,
    kind: string,
    label?: string,
    prestige_url?: string,
  ): Promise<EvidenceOut> => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    if (label) fd.append("label", label);
    if (prestige_url) fd.append("prestige_url", prestige_url);
    const token = getToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/applications/${applicationId}/evidence`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      }
    );
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      throw new ApiError(
        res.status,
        parsed,
        `Upload failed (${res.status})`
      );
    }
    return (await res.json()) as EvidenceOut;
  },
  patch: (evidenceId: string, body: { kind?: string; label?: string; prestige_url?: string }) =>
    request<EvidenceOut>(`/v1/evidence/${evidenceId}`, { method: "PATCH", body }),
  delete: (evidenceId: string) =>
    request<void>(`/v1/evidence/${evidenceId}`, { method: "DELETE" }),
  /** Rebuild a citing paper's 'Citation by others' exhibit from a free copy. */
  rebuildCitingExhibit: (evidenceId: string) =>
    request<EvidenceOut>(`/v1/evidence/${evidenceId}/citing-exhibit`, { method: "POST" }),
  /** Build the exhibit from a copy of the citing paper the user uploads. */
  uploadCitingPdf: async (evidenceId: string, file: File): Promise<EvidenceOut> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}/v1/evidence/${evidenceId}/citing-pdf`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      throw new ApiError(res.status, parsed, (parsed && typeof parsed.detail === "string" && parsed.detail) || `Upload failed (${res.status})`);
    }
    return (await res.json()) as EvidenceOut;
  },
  /** Pull the most-cited papers that cite the scholar's publications from
   *  OpenAlex and store them as reference evidence for Criterion E. */
  autoCitingWorks: (applicationId: string, limit = 10) =>
    request<CitingWorksAutoOut>(
      `/v1/applications/${applicationId}/evidence/citing-works/auto?limit=${limit}`,
      { method: "POST" },
    ),
};

export const criteria = {
  catalog: (applicationType: "eb1a" | "eb2_niw") =>
    request<CriterionCatalogEntry[]>(
      `/v1/criteria/catalog?application_type=${applicationType}`,
      { auth: false }
    ),
  list: (applicationId: string) =>
    request<CriterionAssessmentOut[]>(
      `/v1/applications/${applicationId}/criteria`
    ),
  recompute: (applicationId: string) =>
    request<CriterionAssessmentOut[]>(
      `/v1/applications/${applicationId}/criterion-mapping`,
      { method: "POST" }
    ),
};

export const recommenders = {
  list: () => request<RecommenderOut[]>("/v1/recommenders"),
  create: (body: {
    full_name: string;
    title?: string;
    institution?: string;
    country?: string;
    email?: string;
    relationship: "independent" | "dependent";
    expertise_summary?: string;
    bio?: string | null;
    cited_count?: number | null;
    relationship_evidence?: string | null;
  }) =>
    request<RecommenderOut>("/v1/recommenders", { method: "POST", body }),
  update: (
    id: string,
    body: {
      full_name?: string;
      title?: string;
      institution?: string;
      country?: string;
      email?: string;
      relationship?: "independent" | "dependent";
      expertise_summary?: string;
      bio?: string | null;
      cited_count?: number | null;
      relationship_evidence?: string | null;
    }
  ) => request<RecommenderOut>(`/v1/recommenders/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    request<void>(`/v1/recommenders/${id}`, { method: "DELETE" }),
  /** Letters for an application. Pass `kind` so the contributions flow
   *  and the critical-role flow each resume from their own rows only.
   *  Without `q.scope` the server returns the original petition's letters
   *  only; the RFE letters phase passes { scope: "rfe", rfeCaseId }. */
  letters: (applicationId: string, kind?: LetterKind, q: LetterQuery = {}) =>
    request<RecommendationLetterOut[]>(
      `/v1/applications/${applicationId}/recommendation-letters${letterQuery(kind, q)}`
    ),
  /** SSE draft endpoint for one writer's letter (POST; fetch it with the
   *  Authorization header). `rfeCaseId` drafts the letter for that RFE
   *  response: its own row, never the writer's original letter. */
  draftUrl: (
    applicationId: string,
    recommenderId: string,
    opts: { kind?: LetterKind; rfeCaseId?: string; anchorPublicationIds?: string[] } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.kind && opts.kind !== "contributions") params.set("kind", opts.kind);
    if (opts.rfeCaseId) params.set("rfe_case_id", opts.rfeCaseId);
    if (opts.anchorPublicationIds?.length) params.set("anchor_publication_ids", opts.anchorPublicationIds.join(","));
    const qs = params.toString();
    return `${API_BASE_URL}/v1/applications/${applicationId}/recommendation-letters/${recommenderId}/draft${qs ? `?${qs}` : ""}`;
  },
  /** DOCX of one letter (GET with the Authorization header). */
  docxUrl: (applicationId: string, letterId: string) =>
    `${API_BASE_URL}/v1/applications/${applicationId}/recommendation-letters/${letterId}/docx`,
  patchLetter: (
    applicationId: string,
    letterId: string,
    body: {
      body_markdown?: string;
      status?: string;
      anchor_publication_ids?: string[];
      tone?: "knowledgeable_friend" | "formal_expert" | "direct_concise" | "";
      /** Critical-role questionnaire; replaces the stored answers wholesale. */
      inputs?: Record<string, unknown>;
    }
  ) =>
    request<RecommendationLetterOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}`,
      { method: "PATCH", body }
    ),
  /** Fetch a profile/lab/institution URL and populate the recommender's
   *  title / institution / expertise_summary (focus) / bio from what's
   *  extracted. A bio the scholar already wrote is kept; the extracted
   *  text is appended under a separator. */
  enrich: (recommenderId: string, url: string) =>
    request<RecommenderEnrichResponse>(
      `/v1/recommenders/${recommenderId}/enrich`,
      { method: "POST", body: { url } }
    ),
  /** Upload a recommender CV (PDF or DOCX) and populate title /
   *  institution / expertise_summary (focus) / bio from the extracted
   *  text. The file is NOT stored — only the parsed fields. */
  uploadCv: async (
    recommenderId: string,
    file: File,
  ): Promise<RecommenderEnrichResponse> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = authToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/recommenders/${recommenderId}/cv-upload`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      },
    );
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      const detail =
        typeof parsed === "object" && parsed && "detail" in parsed
          ? (parsed as { detail: unknown }).detail
          : null;
      throw new ApiError(
        res.status,
        parsed,
        typeof detail === "string" ? detail : `Upload failed (${res.status})`,
      );
    }
    return (await res.json()) as RecommenderEnrichResponse;
  },
  /** 4 collaborators + 4 arms-length recommender suggestions, computed
   *  from the user's publications via the Semantic Scholar graph.
   *  Cached server-side for 24h on subtype_data so a repeat visit
   *  doesn't re-hit S2. Pass refresh=true (the StepWriter Retry button)
   *  to bypass the cache. */
  suggestions: (applicationId: string, refresh = false) =>
    request<RecommenderSuggestionsOut>(
      `/v1/applications/${applicationId}/recommender-suggestions${refresh ? "?refresh=true" : ""}`
    ),
  /** Conversational edit — propose span-anchored edits from a natural-language
   *  instruction. Returns a summary + list of { old_text, new_text } edits
   *  the frontend renders as an inline diff with per-change Accept/Reject. */
  suggestEdit: (
    applicationId: string,
    letterId: string,
    body: { instruction: string; current_text: string }
  ) =>
    request<SuggestEditOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}/suggest-edit`,
      { method: "POST", body }
    ),
  /** Email a magic-link review request to the recommender. */
  sendOutreach: (recommenderId: string, letterId: string) =>
    request<{ ok: boolean; message_id: string; magic_link: string; sent_to: string }>(
      `/v1/recommenders/${recommenderId}/letters/${letterId}/send-outreach`,
      { method: "POST" }
    ),
  /** One letter, fresh from the server (e.g. after send-outreach). */
  getLetter: (applicationId: string, letterId: string) =>
    request<RecommendationLetterOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}`
    ),
  /** Undo "mark as sent" (back to approved). 409 once the writer sent
   *  something back. */
  undoMarkSent: (applicationId: string, letterId: string) =>
    request<RecommendationLetterOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}/mark-sent/undo`,
      { method: "POST" }
    ),
  /** Scholar approves a drafted letter (status → SCHOLAR_EDITED). Called
   *  by Letter Studio's "Download & next letter" CTA: the download itself
   *  implies the scholar is taking the draft offline. */
  approveLetter: (applicationId: string, letterId: string) =>
    request<RecommendationLetterOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}/approve`,
      { method: "POST" }
    ),
  /** Scholar marks a letter as sent to the recommender offline (status
   *  → SENT_TO_RECOMMENDER, outreach_sent_at = that day, or now). `sentOn`
   *  is an ISO date ("2026-10-03"); omit for today. */
  markSent: (applicationId: string, letterId: string, sentOn?: string) =>
    request<RecommendationLetterOut>(
      `/v1/applications/${applicationId}/recommendation-letters/${letterId}/mark-sent`,
      { method: "POST", body: sentOn ? { sent_on: sentOn } : undefined }
    ),
  /** URL for the bulk-download endpoint that returns every drafted
   *  letter zipped together. Caller fetches with the Authorization
   *  header (no SSE query-token fallback needed). */
  bulkDocxUrl: (applicationId: string, kind?: LetterKind) =>
    `${API_BASE_URL}/v1/applications/${applicationId}/recommendation-letters/bulk-docx${kind ? `?kind=${kind}` : ""}`,
  /** Letter Studio "Start over" — drop the drafted letters for the
   *  application so the returning-user resume effect doesn't snap
   *  the user back into the batch-review screen. Recommender rows
   *  stay; only the letter rows go. Each flow passes its own `kind`
   *  so resetting the role letters never touches the expert letters
   *  (and vice versa); omitting it deletes every letter of the scope.
   *  The scope defaults to the petition on the server, so this never
   *  deletes a letter drafted for an RFE response. */
  deleteAllLetters: (applicationId: string, kind?: LetterKind, q: LetterQuery = {}) =>
    request<void>(
      `/v1/applications/${applicationId}/recommendation-letters/all${letterQuery(kind, q)}`,
      { method: "DELETE" }
    ),
  /** Scholar uploads the signed PDF (status → SIGNED, received_at = now).
   *  Mirrors the recommender-magic-link path but takes scholar auth. */
  uploadSignedLetter: async (
    applicationId: string,
    letterId: string,
    file: File,
  ): Promise<RecommendationLetterOut> => {
    const fd = new FormData();
    fd.append("file", file);
    const token = authToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/applications/${applicationId}/recommendation-letters/${letterId}/upload-signed`,
      {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      },
    );
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      throw new ApiError(res.status, parsed, `Upload failed (${res.status})`);
    }
    return (await res.json()) as RecommendationLetterOut;
  },
  /** URL for the scholar to download the signed PDF the recommender returned. */
  signedPdfUrl: (recommenderId: string, letterId: string) =>
    `${API_BASE_URL}/v1/recommenders/${recommenderId}/letters/${letterId}/signed-pdf`,
};

/** One numbered exhibit the brief cites, mapped to an uploaded file or a
 *  recommendation letter. `status` is "needs_file" when neither id is set. */
export interface ExhibitIndexEntry {
  number: number;
  title: string;
  /** The sentence of the brief where the exhibit is first cited. */
  context: string;
  source_kind: "evidence" | "letter" | null;
  evidence_item_id: string | null;
  /** More files in the same exhibit (all the publications, all the press articles). */
  extra_evidence_item_ids?: string[];
  letter_id: string | null;
  status: "matched" | "needs_file";
}

export interface ExhibitIndexOut {
  /** True once the scholar has accepted a mapping (stored on the application). */
  accepted: boolean;
  accepted_at: string | null;
  /** True once the brief itself is accepted (status "finalized"). */
  brief_accepted: boolean;
  entries: ExhibitIndexEntry[];
  /** True when the VisaGenius case team accepted the index on the client's behalf. */
  accepted_by_operator?: boolean;
  /** Same check as PetitionLetterOut.exhibit_numbering_issues. */
  numbering_issues?: string[];
}

export const petitionLetter = {
  get: (applicationId: string) =>
    request<PetitionLetterOut>(
      `/v1/applications/${applicationId}/petition-letter`
    ),
  patch: (
    applicationId: string,
    body: { body_markdown?: string; status?: string }
  ) =>
    request<PetitionLetterOut>(
      `/v1/applications/${applicationId}/petition-letter`,
      { method: "PATCH", body }
    ),
  /** Accept the brief as written. Stamps brief_accepted_at on the application. */
  accept: (applicationId: string) =>
    request<PetitionLetterOut>(
      `/v1/applications/${applicationId}/petition-letter`,
      { method: "PATCH", body: { status: "finalized" } }
    ),
  /** Renumber the brief's exhibits 1..N in order of first citation.
   *  Changing the text withdraws an acceptance, as any edit does. */
  renumberExhibits: (applicationId: string) =>
    request<PetitionLetterOut>(
      `/v1/applications/${applicationId}/petition-letter/renumber-exhibits`,
      { method: "POST" }
    ),
  /** The accepted index of exhibits, or one proposed from the brief.
   *  409 until a brief has been drafted. `fresh` re-parses the brief. */
  exhibitIndex: (applicationId: string, fresh = false) =>
    request<ExhibitIndexOut>(
      `/v1/applications/${applicationId}/exhibit-index${fresh ? "?fresh=true" : ""}`
    ),
  /** Save the final number → file mapping. 409 until the brief is accepted. */
  acceptExhibitIndex: (applicationId: string, entries: ExhibitIndexEntry[]) =>
    request<ExhibitIndexOut>(
      `/v1/applications/${applicationId}/exhibit-index`,
      { method: "POST", body: { entries } }
    ),
};

// ─── RFE response (app/api/v1/rfe.py) ─────────────────────────────────────────

export interface RfeConcern {
  n: number;
  /** The concern in the RFE's own words. */
  text: string;
}

export interface RfeMaySubmit {
  text: string;
  note: string | null;
}

export interface RfeClaimedCriterion {
  /** awards | membership | published_material | judging | original_contributions |
   *  authorship | leading_role | high_salary */
  criterion_id: string;
  regulation: string;
  finding: "met" | "insufficient";
  record_provided: string[];
  concerns: RfeConcern[];
  may_submit: RfeMaySubmit[];
}

export interface RfeParsed {
  claimed_criteria: RfeClaimedCriterion[];
  final_merits_requested: boolean;
  other_requests: string[];
  client_answers?: Record<string, string>;
  beneficiary_name?: string;
  parse_source?: string;
  parse_warnings?: string[];
  /** "Received Date" from the notice header (the petition's filing date), ISO. */
  petition_received_on?: string;
}

/** One exhibit of the petition as filed, from its table of exhibits. */
export interface FiledExhibit {
  /** As numbered in the filing ("12", "12-A"). */
  number: string;
  title: string;
  kind: string;
  /** Criterion ids the exhibit was filed under. */
  criteria: string[];
}

/** A recommendation letter in the petition as filed. */
export interface FiledLetter {
  exhibit: string;
  kind: LetterKind;
  writer_name: string | null;
  writer_title: string | null;
  institution: string | null;
  /** False when only the exhibit's title was found, not the letter's text. */
  text_imported: boolean;
  recommender_id: string | null;
}

/** Legacy intake (admin portal): what was imported from a petition filed
 *  before VisaGenius. */
export interface FiledRecordSummary {
  source: "drive" | "upload" | "mixed";
  folder_url: string | null;
  imported_at: string;
  petition_letter: { name: string; chars: number } | null;
  exhibits: FiledExhibit[];
  letters: FiledLetter[];
  /** The criteria the petition letter argues, as its sections name them. */
  criteria_sections: string[];
  supporting_docs: { name: string; pages: number | null; letters_found: number } | null;
  warnings: string[];
}

export interface RecordReviewConcern {
  concern: string;
  /** What the filed record already holds that answers it; null when nothing. */
  in_record: string | null;
  in_record_exhibits: string[];
  /** What is still missing. */
  gap: string;
  answer_with: string[];
}

export interface RecordReviewCriterion {
  criterion_id: string;
  title: string;
  finding: "met" | "insufficient";
  filed_argument: string;
  exhibits_filed: string[];
  concerns: RecordReviewConcern[];
  /** Writers of filed letters to ask again, and what to ask. */
  supplemental_letters: { writer_name: string; exhibit: string; ask: string }[];
  new_letters: { from: string; ask: string }[];
}

/** Legacy intake: the filed record compared with the RFE, per criterion. */
export interface RecordReview {
  generated_at: string;
  /** "demo" when the server ran without Claude: placeholder findings. */
  source: "claude" | "demo";
  criteria: RecordReviewCriterion[];
  client_todo: string[];
}

export interface RfeCaseOut {
  id: string;
  application_id: string;
  kind: "rfe" | "noid";
  status: "uploaded" | "parsed" | "triaged" | "drafting" | "drafted" | "accepted" | "packaged";
  notice_file_name: string | null;
  receipt_number: string | null;
  a_number: string | null;
  issued_on: string | null;
  due_on: string | null;
  /** Negative once the deadline has passed; null when not read. */
  days_until_due: number | null;
  premium_processing: boolean;
  service_center_address: string | null;
  parsed: RfeParsed;
  parse_source: string | null;
  parse_warnings: string[];
  selected_criteria: string[];
  met_count: number;
  total_as_supplemented: number;
  fee_accepted_at: string | null;
  fee_cents: number;
  response_markdown: string | null;
  response_accepted_at: string | null;
  exhibit_index_accepted_at: string | null;
  package_assembled_at: string | null;
  created_at: string;
  updated_at: string;
  /** Legacy intake: the petition as filed, once imported in the admin portal. */
  filed_record?: FiledRecordSummary | null;
  /** Legacy intake: the filed record compared with this RFE. */
  record_review?: RecordReview | null;
}

export interface RfeTodoItem {
  id: string;
  type: "document" | "letter" | "print";
  text: string;
  note: string | null;
  source: "rfe" | "method";
  done: boolean;
}

export interface RfeSuggestedLetter {
  kind: string;
  title: string;
  from: string;
  questions: string[];
  /** Original contributions / critical role: true once the letters phase
   *  has an approved letter for this criterion. */
  done: boolean;
}

/** The letters drafted for this response for one criterion (letters phase). */
export interface RfeLetterGroupCounts {
  kind: LetterKind;
  drafted: number;
  approved: number;
  signed: number;
}

export interface RfeEvidencePlanCriterion {
  criterion_id: string;
  title: string;
  regulation: string;
  concerns: RfeConcern[];
  on_file: { id: string; label: string; kind: string; is_new: boolean }[];
  letters_on_file: {
    letter_id: string; recommender: string; status: string; is_new: boolean;
    kind?: LetterKind;
    /** Drafted for this response (not the original petition). */
    rfe?: boolean;
  }[];
  todo: RfeTodoItem[];
  suggested_letters: RfeSuggestedLetter[];
  upload_kinds: string[];
  /** Set for the criteria whose letters are gathered in the letters phase. */
  letter_group: RfeLetterGroupCounts | null;
}

export interface RfeEvidencePlanOut {
  selected: string[];
  met_count: number;
  selected_count: number;
  total_as_supplemented: number;
  needs_three: boolean;
  criteria: RfeEvidencePlanCriterion[];
  letter_studio_href: string;
}

/** One new exhibit the response cites ("7" or "7-A"), mapped to a file. */
export interface RfeExhibitIndexEntry {
  number: number;
  sub_letter: string | null;
  label: string;
  title: string;
  context: string;
  criterion_ids: string[];
  source_kind: "evidence" | "letter" | null;
  evidence_item_id: string | null;
  letter_id: string | null;
  status: "matched" | "needs_file";
}

export interface RfeExhibitIndexOut {
  accepted: boolean;
  accepted_at: string | null;
  response_accepted: boolean;
  /** "Initial Exhibit N" numbers the response refers back to; not new rows. */
  initial_exhibits_referenced: number[];
  entries: RfeExhibitIndexEntry[];
}

export const rfe = {
  /** The open RFE on the case; 404 until a notice is uploaded. */
  get: (applicationId: string) => request<RfeCaseOut>(`/v1/applications/${applicationId}/rfe`),
  /** Upload the notice PDF. The server parses it before answering (a few seconds). */
  upload: async (
    applicationId: string,
    file: File,
    opts: { kind?: "rfe" | "noid"; replace?: boolean } = {},
  ): Promise<RfeCaseOut> => {
    const fd = new FormData();
    fd.append("file", file);
    if (opts.kind) fd.append("kind", opts.kind);
    const token = getToken();
    const res = await fetch(
      `${API_BASE_URL}/v1/applications/${applicationId}/rfe${opts.replace ? "?replace=true" : ""}`,
      { method: "POST", headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd },
    );
    if (!res.ok) {
      const parsed = await res.json().catch(() => null);
      const detail =
        typeof parsed === "object" && parsed && "detail" in parsed
          ? (parsed as { detail: unknown }).detail
          : null;
      throw new ApiError(
        res.status,
        parsed,
        typeof detail === "string" ? detail : `Upload failed (${res.status})`,
      );
    }
    return (await res.json()) as RfeCaseOut;
  },
  patch: (
    applicationId: string,
    body: {
      selected_criteria?: string[];
      fee_accepted?: boolean;
      response_markdown?: string;
      client_answers?: Record<string, string>;
    },
  ) => request<RfeCaseOut>(`/v1/applications/${applicationId}/rfe`, { method: "PATCH", body }),
  /** Records fee_accepted_at. Payment itself stays manual. */
  acceptFee: (applicationId: string) =>
    request<RfeCaseOut>(`/v1/applications/${applicationId}/rfe`, {
      method: "PATCH",
      body: { fee_accepted: true },
    }),
  evidencePlan: (applicationId: string) =>
    request<RfeEvidencePlanOut>(`/v1/applications/${applicationId}/rfe/evidence-plan`, {
      method: "POST",
    }),
  /** SSE endpoint for the response chain; fetch it with the Authorization header. */
  draftUrl: (applicationId: string) => `${API_BASE_URL}/v1/applications/${applicationId}/rfe/draft`,
  acceptResponse: (applicationId: string) =>
    request<RfeCaseOut>(`/v1/applications/${applicationId}/rfe/accept-response`, {
      method: "POST",
    }),
  exhibitIndex: (applicationId: string, fresh = false) =>
    request<RfeExhibitIndexOut>(
      `/v1/applications/${applicationId}/rfe/exhibit-index${fresh ? "?fresh=true" : ""}`,
    ),
  acceptExhibitIndex: (applicationId: string, entries: RfeExhibitIndexEntry[]) =>
    request<RfeExhibitIndexOut>(`/v1/applications/${applicationId}/rfe/exhibit-index`, {
      method: "POST",
      body: { entries },
    }),
  /** ZIP download; 402 until the fee is confirmed, 409 until response and index are accepted. */
  packageUrl: (applicationId: string) =>
    `${API_BASE_URL}/v1/applications/${applicationId}/rfe/package`,
};

export interface PublicationOut {
  id: string;
  scholar_profile_id: string;
  title: string;
  venue: string | null;
  year: number | null;
  authors: string | null;
  scholar_role: string | null;
  citation_count: number | null;
  doi: string | null;
  url: string | null;
}

export interface PublicationIn {
  title: string;
  venue?: string | null;
  year?: number | null;
  authors?: string | null;
  scholar_role?: string | null;
  citation_count?: number | null;
  doi?: string | null;
  url?: string | null;
}

// Mirrors ScholarFetchOut in the backend schema. Identical shape to
// ScholarParseResult so it drops into the same review screen unchanged.
export interface ScholarFetchPubOut {
  title: string;
  authors: string | null;
  venue: string | null;
  year: number | null;
  citation_count: number | null;
  scholar_url: string | null;
  authorship_role: "first" | "last" | "middle" | "solo" | "unknown" | null;
}

export interface ScholarFetchOut {
  user_id: string;
  display_name: string | null;
  affiliation: string | null;
  citations_all: number | null;
  h_index: number | null;
  i10_index: number | null;
  publications: ScholarFetchPubOut[];
}

export interface ScholarImportOut {
  user_id: string;
  display_name: string | null;
  affiliation: string | null;
  citations_all: number | null;
  h_index: number | null;
  i10_index: number | null;
  publications_inserted: number;
}

export const publications = {
  list: () => request<PublicationOut[]>("/v1/publications"),
  create: (body: PublicationIn) =>
    request<PublicationOut>("/v1/publications", { method: "POST", body }),
  update: (id: string, body: PublicationIn) =>
    request<PublicationOut>(`/v1/publications/${id}`, {
      method: "PATCH",
      body,
    }),
  remove: (id: string) =>
    request<void>(`/v1/publications/${id}`, { method: "DELETE" }),
  bulkImport: (rows: PublicationIn[]) =>
    request<PublicationOut[]>("/v1/publications/bulk-import", {
      method: "POST",
      body: rows,
    }),
  /** Resolve the scholar on OpenAlex and upsert their works (DOIs, venues,
   *  citation counts). Safe to call repeatedly; 404 when unresolved. */
  openalexImport: (body: { orcid?: string | null } = {}) =>
    request<OpenAlexImportOut>("/v1/publications/openalex-import", {
      method: "POST",
      body,
    }),
  /** One-shot: fetch Scholar, clear old pubs, insert new ones server-side. */
  scholarImport: (scholarUrl: string) =>
    request<ScholarImportOut>("/v1/publications/scholar-import", {
      method: "POST",
      body: { scholar_url: scholarUrl },
    }),
  fetchScholar: (scholarUrl: string) =>
    request<ScholarFetchOut>("/v1/publications/scholar-fetch", {
      method: "POST",
      body: { scholar_url: scholarUrl },
    }),
  clearAll: () =>
    request<void>("/v1/publications", { method: "DELETE" }),
  dedupe: () =>
    request<{ removed: number; kept: number }>("/v1/publications/dedupe", {
      method: "POST",
    }),
};

export interface MarketplaceAttorneyOut {
  slug: string;
  display_name: string;
  profile: Record<string, string>;
}

export interface EngagementOut {
  id: string;
  scholar_tenant_id: string;
  attorney_tenant_id: string;
  application_id: string;
  status: string;
  scholar_message: string | null;
  attorney_proposal: string | null;
  attorney_fee_quote_cents: number | null;
}

export const marketplace = {
  attorneys: () =>
    request<MarketplaceAttorneyOut[]>("/v1/marketplace/attorneys"),
};

export const engagements = {
  list: () => request<EngagementOut[]>("/v1/engagements"),
  get: (id: string) => request<EngagementOut>(`/v1/engagements/${id}`),
  request: (body: {
    application_id: string;
    attorney_tenant_slug: string;
    scholar_message?: string | null;
  }) =>
    request<EngagementOut>("/v1/engagements", { method: "POST", body }),
  propose: (
    id: string,
    body: { attorney_proposal: string; fee_quote_usd: number },
  ) =>
    request<EngagementOut>(`/v1/engagements/${id}/propose`, {
      method: "POST",
      body,
    }),
  accept: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/accept`, {
      method: "POST",
    }),
  start: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/start`, {
      method: "POST",
    }),
  file: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/file`, {
      method: "POST",
    }),
  decline: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/decline`, {
      method: "POST",
    }),
  withdraw: (id: string) =>
    request<EngagementOut>(`/v1/engagements/${id}/withdraw`, {
      method: "POST",
    }),
  workspace: (id: string) =>
    request<EngagementWorkspace>(`/v1/engagements/${id}/workspace`),
};

export interface EngagementWorkspace {
  engagement: {
    id: string;
    status: string;
    scholar_tenant_id: string;
    attorney_tenant_id: string;
    scholar_message: string | null;
    attorney_proposal: string | null;
    attorney_fee_quote_cents: number | null;
  };
  application: {
    id: string;
    application_type: string;
    status: string;
    title: string;
  };
  scholar: {
    full_name: string | null;
    field_of_study: string | null;
    current_institution: string | null;
  } | null;
  // Present only on ACCEPTED+ engagements:
  evidence?: Array<{
    id: string;
    kind: string;
    label: string;
    file_name: string;
    size_bytes: number;
    parse_status: string;
  }>;
  criterion_assessments?: Array<{
    criterion_key: string;
    score: string;
    rationale: string | null;
  }>;
  petition_letter?: {
    status: string;
    body_markdown: string | null;
  } | null;
  recommendation_letters?: Array<{
    id: string;
    recommender_name: string | null;
    status: string;
    body_markdown: string | null;
  }>;
}

export interface DraftEventOut {
  id: string;
  application_id: string;
  kind: string;
  letter_id: string | null;
  outcome: "success" | "demo_stub" | "error";
  metadata_json: Record<string, unknown>;
  body_length: number;
  error_message: string | null;
  user_id: string;
  created_at: string;
}

export const audit = {
  draftEvents: (applicationId: string) =>
    request<DraftEventOut[]>(
      `/v1/applications/${applicationId}/draft-events`,
    ),
};

export function packetDownloadUrl(applicationId: string): string {
  return `${API_BASE_URL}/v1/applications/${applicationId}/packet`;
}

export function streamingUrl(path: string): string {
  // EventSource can't set Authorization headers, so we put the JWT in
  // the query string for SSE endpoints. The drafting router validates
  // it the same way as the header path.
  const token = getToken();
  const sep = path.includes("?") ? "&" : "?";
  return `${API_BASE_URL}${path}${sep}access_token=${encodeURIComponent(token ?? "")}`;
}

/** The message in a FastAPI error body: `detail` as text, or the messages
 *  of a validation-error list. */
function detailMessage(body: unknown): string | null {
  const detail = typeof body === "object" && body !== null && "detail" in body ? (body as { detail: unknown }).detail : null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => (d && typeof d === "object" && "msg" in d ? String((d as { msg: unknown }).msg) : "")).filter(Boolean);
    return msgs.length ? msgs.join("; ") : null;
  }
  return null;
}

/** Read a `text/event-stream` body (`event: <name>\ndata: <json>\n\n`
 *  frames, as the API's SSEEvent writes them) and hand each event to
 *  `onEvent`. Frames without a JSON object for data are skipped. */
export async function readEventStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (name: string, data: Record<string, unknown>) => void,
): Promise<void> {
  const emit = (frame: string) => {
    let name = "message";
    const data: string[] = [];
    for (const line of frame.split("\n")) {
      if (line.startsWith("event:")) name = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trim());
    }
    if (!data.length) return;
    let parsed: unknown;
    try { parsed = JSON.parse(data.join("\n")); } catch { return; }
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) onEvent(name, parsed as Record<string, unknown>);
  };
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf = (buf + decoder.decode(value, { stream: true })).replace(/\r\n/g, "\n");
    const frames = buf.split("\n\n");
    buf = frames.pop() ?? "";
    frames.forEach(emit);
  }
  buf += decoder.decode();
  if (buf.trim()) emit(buf);
}

/** Fetch an SSE endpoint (EventSource cannot POST or send headers) with the
 *  bearer token and read its events. A refusal before the stream starts
 *  throws ApiError carrying the server's `detail`. */
export async function fetchEventStream(
  url: string,
  init: RequestInit,
  onEvent: (name: string, data: Record<string, unknown>) => void,
): Promise<void> {
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token && !headers.has("Authorization")) headers.set("Authorization", `Bearer ${token}`);
  if (!headers.has("Accept")) headers.set("Accept", "text/event-stream");
  const res = await fetch(url, { ...init, headers });
  if (!res.ok) {
    const parsed: unknown = await res.json().catch(() => null);
    throw new ApiError(res.status, parsed, detailMessage(parsed) ?? `Request failed (${res.status})`);
  }
  if (res.body) await readEventStream(res.body, onEvent);
}

// ── Draft jobs ─────────────────────────────────────────────────────────────
// Drafts run on the server as jobs, so leaving the page never stops one.
// Every draft stream opens with a `job` event and then replays the job from
// its start, so a page reattaching to a draft renders it exactly as if it
// had started it.

export type GenerationJobKind = "recommendation_letter" | "petition_letter" | "rfe_response";

export interface GenerationJobOut {
  id: string;
  kind: GenerationJobKind;
  /** The letter, petition letter or RFE case the job writes. */
  target_id: string | null;
  status: "running" | "done" | "failed" | "interrupted";
  progress: { section?: string; step?: number; total?: number; title?: string } | null;
  chars: number;
  error: string | null;
  attempts: number;
  created_at: string;
  heartbeat_at: string | null;
  finished_at: string | null;
  /** Letters only. */
  recommender_id: string | null;
  letter_kind: LetterKind | null;
  rfe_case_id: string | null;
}

export const jobs = {
  /** Drafts running now for this application (`active: false` adds the
   *  ones finished in the last 24 hours). */
  list: (applicationId: string, opts: { active?: boolean } = {}) =>
    request<GenerationJobOut[]>(
      `/v1/applications/${applicationId}/jobs?active=${opts.active === false ? "false" : "true"}`,
    ),
  /** SSE: the job's events so far, then live ones (GET, Authorization header). */
  eventsUrl: (applicationId: string, jobId: string) =>
    `${API_BASE_URL}/v1/applications/${applicationId}/jobs/${jobId}/events`,
};

const REATTACH_TRIES = 3;

/** Run or watch a draft job: `url`/`init` open the stream (a draft
 *  endpoint's POST, or `jobs.eventsUrl` with GET). Each `job` event means
 *  "the job's events start over from here": reset what you have collected
 *  when it arrives. The draft carries on on the server when the connection
 *  drops, so a stream that breaks before its last `done` or `error`
 *  reattaches to the job's events (a few tries) instead of failing. */
export async function followDraftJob(
  applicationId: string,
  url: string,
  init: RequestInit,
  onEvent: (name: string, data: Record<string, unknown>) => void,
): Promise<void> {
  // Written from the event handler, so kept in an object (no narrowing).
  const seen: { jobId: string | null; last: string } = { jobId: null, last: "" };
  const handle = (name: string, data: Record<string, unknown>) => {
    if (name === "job" && typeof data.job_id === "string") seen.jobId = data.job_id;
    seen.last = name;
    onEvent(name, data);
  };
  for (let attempt = 0; ; attempt += 1) {
    let broke: unknown = null;
    try {
      if (attempt === 0 || !seen.jobId) await fetchEventStream(url, init, handle);
      else await fetchEventStream(jobs.eventsUrl(applicationId, seen.jobId), { method: "GET", signal: init.signal }, handle);
    } catch (e) {
      broke = e;
    }
    const finished = !broke && (seen.last === "done" || seen.last === "error");
    if (finished) return;
    const canRetry = !!seen.jobId && !init.signal?.aborted && !(broke instanceof ApiError) && attempt < REATTACH_TRIES;
    if (!canRetry) {
      if (broke) throw broke;
      return;
    }
    await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
    if (init.signal?.aborted) throw broke ?? new DOMException("Aborted", "AbortError");
  }
}

export const apiBaseUrl = API_BASE_URL;

// ── Operator (admin portal) ────────────────────────────────────────────────

export type ServiceProduct = "eb1a" | "niw" | "eb1a_niw";
export type WaitingOn = "client" | "visagenius" | "uscis";

export interface Charge { id: string; on: string; description: string; amount_cents: number }
export interface Payment {
  id: string; on: string; amount_cents: number; method: string; note: string | null;
  receipt_no: string | null; receipt_sent_at: string | null; receipt_sent_to: string | null;
}
export interface LogEntry { id: string; on: string; kind: "call" | "email" | "note"; text: string }
export interface Statement {
  id: string; statement_no: string; period_start: string; period_end: string; sent_at: string;
  sent_to: string; translated_to: string | null; total_charges_cents: number; total_paid_cents: number;
}

/** The done-for-you engagement, kept on the case for the admin portal. */
export interface ServiceRecord {
  product: ServiceProduct | null;
  phone: string | null;
  drive_folder_url: string | null;
  kickoff_sent_on: string | null;
  /** When the "you're in" email (pay + upload) went out, after they signed. */
  next_steps_sent_on: string | null;
  /** Milestone id → ISO date it happened, or null. */
  milestones: Record<string, string | null>;
  next_action: string | null;
  next_action_due: string | null;
  waiting_on: WaitingOn | null;
  notes: string;
  archived: boolean;
  /** Receipts and statements (Bus. & Prof. Code § 22442.1). */
  native_language: string | null;
  charges: Charge[];
  payments: Payment[];
  statements: Statement[];
  log: LogEntry[];
  /** Letter id → dates the recommender was reminded to sign. */
  letter_chases?: Record<string, string[]>;
}

/** What is on file under one criterion, in plain words. */
export interface EvidenceGroup {
  key: string;
  label: string;
  /** e.g. ["3 papers citing them", "1 patent"] */
  items: string[];
  letters: number;
  letters_signed: number;
}

export type LetterStep = "drafting" | "with_client" | "ready_to_send" | "with_signer" | "signed";

/** One recommendation letter, from the operator's side. */
export interface LetterRow {
  id: string;
  recommender_name: string;
  recommender_email: string | null;
  recommender_title: string | null;
  institution: string | null;
  independent: boolean;
  kind: string;
  supports: string;
  step: LetterStep;
  sent_on: string | null;
  signed_on: string | null;
  updated_on: string;
  chased_on: string[];
}

export interface OperatorCase {
  tenant_id: string;
  workspace_name: string;
  client_name: string;
  client_email: string | null;
  application_id: string | null;
  done_stages: string[];
  setup: Partial<CaseSetup>;
  service: ServiceRecord;
  files_on_file: number;
  letters_drafted: number;
  letters_signed: number;
  /** From the eligibility read: criteria met and the total scored. */
  criteria_met: number | null;
  criteria_total: number | null;
  /** From the Case assessment: the plan they asked to start ("EB-1A", …). */
  plan_requested?: string | null;
  /** Answers a person should review before the kickoff (J-1, past denial, …). */
  assessment_flags?: string[];
  /** The evidence inventory, grouped by the criterion each item supports. */
  evidence: EvidenceGroup[];
  /** Letters out with recommenders to sign, and since when the oldest waited. */
  letters_with_signers: number;
  letters_waiting_since: string | null;
  created_at: string;
  is_mine: boolean;
  /** When the next two-month statement is owed; null once archived. */
  statement_due_on: string | null;
  balance_cents: number;
  key_dates: { passport_expiry: string | null; status_until: string | null };
}

export interface OperatorCasePatch {
  client_name?: string;
  client_email?: string;
  service?: Partial<Omit<ServiceRecord, "milestones" | "payments" | "statements" | "log">> & { milestones?: Record<string, string | null>; log?: Omit<LogEntry, "id">[] | LogEntry[] };
}

/** Open a PDF the API serves behind the bearer token in a new tab. */
export async function openAuthedPdf(path: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}${path}`, { headers: { Authorization: `Bearer ${getToken() ?? ""}` } });
  if (!res.ok) throw new ApiError(res.status, null, `Could not load the PDF (${res.status}).`);
  const url = URL.createObjectURL(await res.blob());
  window.open(url, "_blank", "noopener");
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export type LeadStatus = "new" | "contacted" | "not_interested";

/** Someone who made their own workspace on the website and isn't a client yet. */
export interface ProspectRow {
  tenant_id: string;
  name: string;
  email: string | null;
  /** False when they asked for a sign-in code but never used it. */
  signed_in: boolean;
  first_seen_at: string;
  last_signed_in_at: string | null;
  google_scholar_id: string | null;
  criteria_met: number | null;
  criteria_total: number | null;
  /** From the Case assessment: the plan they asked to start ("EB-1A", …). */
  plan_requested?: string | null;
  /** Answers a person should review before the kickoff (J-1, past denial, …). */
  assessment_flags?: string[];
  files_on_file: number;
  letters_drafted: number;
  done_stages: string[];
  lead_status: LeadStatus;
  lead_note: string | null;
  contacted_on: string | null;
  /** The plan they chose on the website (eb1a, eb1a_niw, attorney), if any. */
  interest: string | null;
  interest_on: string | null;
}

export const operator = {
  cases: () => request<OperatorCase[]>("/v1/operator/cases"),
  prospects: (includeClosed = false) => request<ProspectRow[]>(`/v1/operator/prospects${includeClosed ? "?include_closed=true" : ""}`),
  patchProspect: (tenantId: string, body: { status?: LeadStatus; note?: string }) =>
    request<ProspectRow>(`/v1/operator/prospects/${encodeURIComponent(tenantId)}`, { method: "PATCH", body }),
  case: (tenantId: string) => request<OperatorCase>(`/v1/operator/cases/${encodeURIComponent(tenantId)}`),
  createCase: (body: { client_name: string; client_email: string; product: ServiceProduct; phone?: string; drive_folder_url?: string; send_kickoff?: boolean }) =>
    request<OperatorCase>("/v1/operator/cases", { method: "POST", body }),
  patchCase: (tenantId: string, body: OperatorCasePatch) =>
    request<OperatorCase>(`/v1/operator/cases/${encodeURIComponent(tenantId)}`, { method: "PATCH", body }),
  openCase: (tenantId: string) =>
    request<TokenOut>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/open`, { method: "POST" }),
  sendKickoff: (tenantId: string) =>
    request<OperatorCase>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/kickoff`, { method: "POST" }),
  /** The kickoff a client would get, sent to you and marked TEST; nothing is created. */
  /** They signed: marks the agreement signed and emails the pay + upload steps. */
  sendNextSteps: (tenantId: string) =>
    request<OperatorCase>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/next-steps`, { method: "POST" }),
  testKickoff: (body: { client_name: string; product: ServiceProduct; email?: "kickoff" | "next_steps" }) =>
    request<{ sent_to: string; is_test: boolean }>("/v1/operator/kickoffs/test", { method: "POST", body }),
  addPayment: (tenantId: string, body: { on: string; amount_cents: number; method: string; note?: string; send_receipt: boolean }) =>
    request<OperatorCase>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/payments`, { method: "POST", body }),
  deletePayment: (tenantId: string, paymentId: string) =>
    request<OperatorCase>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/payments/${paymentId}`, { method: "DELETE" }),
  receiptPdfPath: (tenantId: string, paymentId: string) =>
    `/v1/operator/cases/${encodeURIComponent(tenantId)}/payments/${paymentId}/receipt.pdf`,
  statementPdfPath: (tenantId: string, start?: string, end?: string) =>
    `/v1/operator/cases/${encodeURIComponent(tenantId)}/statement.pdf?` +
    new URLSearchParams({ ...(start ? { period_start: start } : {}), ...(end ? { period_end: end } : {}) }).toString(),
  sendStatement: (tenantId: string, body: { period_start?: string; period_end?: string }) =>
    request<OperatorCase>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/statements`, { method: "POST", body }),
  letters: (tenantId: string) => request<LetterRow[]>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/letters`),
  chaseLetter: (tenantId: string, letterId: string) =>
    request<LetterRow[]>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/letters/${letterId}/chase`, { method: "POST" }),
  setLetterStep: (tenantId: string, letterId: string, step: "ready_to_send" | "with_signer" | "signed") =>
    request<LetterRow[]>(`/v1/operator/cases/${encodeURIComponent(tenantId)}/letters/${letterId}/step`, { method: "POST", body: { step } }),
};

// ── Operator: RFE on a case filed before VisaGenius (legacy intake) ─────────

export interface OperatorRfeStatus {
  /** Whether the server can read Drive folders shared with its service account. */
  drive: { configured: boolean; service_account_email: string | null };
  folder_url: string | null;
  client_email: string | null;
  /** The open RFE case on the client's application, if any. */
  rfe: RfeCaseOut | null;
  rfe_kickoff_sent_on: string | null;
}

/** The parts of a filing a folder scan looks for. */
export type FolderRole = "notice" | "petition_letter" | "exhibit_table" | "supporting_docs";

export interface FoundFile {
  id: string;
  name: string;
  /** Where the file sits inside the folder. */
  path: string;
  mime_type: string;
  size_bytes: number | null;
  modified_time: string | null;
}

export interface FolderScan {
  folder_id: string;
  folder_url: string;
  files_seen: number;
  found: Record<FolderRole, FoundFile | null>;
  warnings: string[];
}

export interface RfeKickoffEmail {
  to: string | null;
  subject: string;
  html: string;
  text: string;
}

/** One event of the import stream. `error` and `done` end it. */
export type RfeImportEvent =
  | { type: "progress"; step: number; total: number; title: string }
  | { type: "warning"; message: string }
  | { type: "error"; message: string }
  | { type: "done"; rfe_case_id: string; filed_record: FiledRecordSummary | null; record_review: RecordReview | null };

export interface RfeImportInput {
  /** Read the filing from this Drive folder (shared with the service account). */
  folderUrl?: string | null;
  /** Start a new RFE case from the notice instead of keeping the open one. */
  replace?: boolean;
  notice?: File | null;
  petitionLetter?: File | null;
  exhibitTable?: File | null;
}

const operatorRfePath = (tenantId: string) => `/v1/operator/cases/${encodeURIComponent(tenantId)}/rfe`;

export const operatorRfe = {
  status: (tenantId: string) => request<OperatorRfeStatus>(operatorRfePath(tenantId)),
  /** Find the notice, petition letter, table of exhibits and supporting
   *  documents in the folder, without importing anything. */
  scan: (tenantId: string, folderUrl: string | null) =>
    request<FolderScan>(`${operatorRfePath(tenantId)}/scan`, { method: "POST", body: { folder_url: folderUrl } }),
  /** Import the filing (folder and/or uploads) and compare it with the RFE,
   *  streaming progress. Throws ApiError when refused before it starts. */
  importRecord: (tenantId: string, input: RfeImportInput, onEvent: (e: RfeImportEvent) => void, signal?: AbortSignal) => {
    const fd = new FormData();
    if (input.folderUrl) fd.append("folder_url", input.folderUrl);
    fd.append("replace", input.replace ? "true" : "false");
    if (input.notice) fd.append("notice", input.notice);
    if (input.petitionLetter) fd.append("petition_letter", input.petitionLetter);
    if (input.exhibitTable) fd.append("exhibit_table", input.exhibitTable);
    const text = (v: unknown) => (typeof v === "string" ? v : "");
    const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
    return fetchEventStream(`${API_BASE_URL}${operatorRfePath(tenantId)}/import`, { method: "POST", body: fd, signal }, (name, d) => {
      if (name === "progress") onEvent({ type: "progress", step: num(d.step), total: num(d.total), title: text(d.title) });
      else if (name === "warning") onEvent({ type: "warning", message: text(d.message) });
      else if (name === "error") onEvent({ type: "error", message: text(d.message) || "The import failed." });
      else if (name === "done") onEvent({ type: "done", rfe_case_id: text(d.rfe_case_id), filed_record: (d.filed_record ?? null) as FiledRecordSummary | null, record_review: (d.record_review ?? null) as RecordReview | null });
    });
  },
  kickoffPreview: (tenantId: string, includeFee: boolean) =>
    request<RfeKickoffEmail>(`${operatorRfePath(tenantId)}/kickoff?include_fee=${includeFee}`),
  /** 409 without an open RFE case; 422 without a client email. */
  sendKickoff: (tenantId: string, body: { include_fee: boolean; note: string | null }) =>
    request<OperatorRfeStatus>(`${operatorRfePath(tenantId)}/kickoff`, { method: "POST", body }),
};
